import { useEffect } from 'react';
import { useSettings } from '../lib/usePublicData';
import { readConsent, onConsentChange } from './CookieConsent';

/**
 * Analytics — loads Google Tag Manager, Google Analytics 4, and Meta Pixel scripts
 * but only after the user accepts cookies. Listens for consent changes so toggling
 * accept ↔ decline works without a page reload.
 *
 * IDs configured via admin Settings:
 *   - settings.gtm_container_id    (e.g. "GTM-XXXXXX")
 *   - settings.google_analytics_id (e.g. "G-XXXXXXXXXX")
 *   - settings.facebook_pixel_id   (e.g. "123456789012345")
 *
 * Always ensures `window.dataLayer` exists so form submits can push events
 * even before GTM is loaded — GTM picks up the queued events when it boots.
 */

function ensureDataLayer() {
    if (!window.dataLayer) window.dataLayer = [];
}

function loadScript(src, id, extra = {}) {
    if (document.getElementById(id)) return;
    const s = document.createElement('script');
    s.async = true;
    s.id = id;
    s.src = src;
    Object.entries(extra).forEach(([k, v]) => s.setAttribute(k, v));
    document.head.appendChild(s);
}

function injectInlineScript(id, code) {
    if (document.getElementById(id)) return;
    const s = document.createElement('script');
    s.id = id;
    s.text = code;
    document.head.appendChild(s);
}

function loadGTM(containerId) {
    if (!containerId) return;
    ensureDataLayer();
    injectInlineScript(
        'gtm-inline',
        `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${containerId}');`
    );
}

function loadGA4(measurementId) {
    if (!measurementId) return;
    ensureDataLayer();
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`, 'ga4-script');
    injectInlineScript(
        'ga4-inline',
        `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} window.gtag = gtag; gtag('js', new Date()); gtag('config', '${measurementId}', {anonymize_ip: true});`
    );
}

function loadPixel(pixelId) {
    if (!pixelId) return;
    injectInlineScript(
        'fbpx-inline',
        `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init', '${pixelId}');fbq('track', 'PageView');`
    );
}

export default function Analytics() {
    const { data: settings } = useSettings();

    useEffect(() => {
        // Always seed dataLayer so form submits can push events even pre-consent.
        ensureDataLayer();
    }, []);

    useEffect(() => {
        if (!settings) return;
        const gtm = (settings.gtm_container_id || '').trim();
        const ga4 = (settings.google_analytics_id || '').trim();
        const pixel = (settings.facebook_pixel_id || '').trim();
        if (!gtm && !ga4 && !pixel) return;

        const apply = (consent) => {
            if (consent !== 'accepted') return;
            if (gtm) loadGTM(gtm);
            if (ga4) loadGA4(ga4);
            if (pixel) loadPixel(pixel);
        };

        apply(readConsent());
        const unsub = onConsentChange(apply);
        return unsub;
    }, [settings]);

    return null;
}

/**
 * Helper used by form components — push a marketing event to the dataLayer.
 * Safe to call regardless of whether GTM is loaded.
 */
export function pushDataLayerEvent(event, payload = {}) {
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event, ...payload });
        // Also fire Meta Pixel "Lead" if available + accepted
        if (typeof window.fbq === 'function' && (event === 'brochure_request_submit' || event === 'contact_message_submit')) {
            window.fbq('track', 'Lead', payload);
        }
    } catch (e) {
        /* noop */
    }
}
